import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Maybe } from 'graphql/jsutils/Maybe'
import { When } from 'react-if'
import { Image } from '@/atoms/Image'
import { CaptionMD, ParagraphLG } from '@/atoms/Text'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { useGuildMember } from '@/services/GuildUserService'
import { FeaturedProject } from '@/types/codegen-contentful'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { FeaturedProjectCarousel } from './FeaturedProjectCarousel'
import { FeaturedProjectGuildButtonGroup } from './FeaturedProjectGuildButtonGroup'
import { FeaturedProjectGuildLabel } from './FeaturedProjectGuildLabel'
import { FeaturedProjectLabel } from './FeaturedProjectLabel'
import { FeaturedProjectLearnMoreButtonGroup } from './FeaturedProjectLearnMoreButtonGroup'
import { FeaturedProjectTicketsButtonGroup } from './FeaturedProjectTicketsButtonGroup'
import { FeaturedProjectWatchButtonGroup } from './FeaturedProjectWatchButtonGroup'

interface FeaturedProjectCallToActionProps {
  featuredProject: FeaturedProject
  position?: string
  shorterDisplay?: boolean
  videoBackground?: boolean
}

export const FeaturedProjectCallToAction: React.FC<FeaturedProjectCallToActionProps> = ({
  featuredProject,
  position,
  shorterDisplay = false,
  videoBackground = false,
}) => {
  const [stockData, setStockData] = useState<string | null>(null)
  const { isSubscribedToPifOrGuild, isGuildMember } = useGuildMember()
  const NYSE_FEATURED_PROJECT_ID = '50LrBwlvYpU6nYfi49mDqm'
  const {
    heading,
    guildHeading,
    logoHeight,
    logoUrl,
    logoWidth,
    pillLabel,
    showGuildCta,
    showTicketsCta,
    subHeading,
    showWatchCta,
    showLearnMoreCta,
  } = featuredProject

  const imageHeight = getImageHeight(videoBackground, shorterDisplay, logoHeight)
  const imageWidth = getImageWidth(videoBackground, shorterDisplay, logoWidth)
  useEffect(() => {
    if (featuredProject?.sys?.id !== NYSE_FEATURED_PROJECT_ID) {
      if (stockData) setStockData(null)
      return
    }

    const url = 'https://www.b2i.us/b2i/QuoteFeed.asp?b=2996&sdiv=QuoteDiv&api=Ro6Uc7Ne3&s=ANGX&f=7&d=1&dl=2'
    ;(async () => {
      try {
        const res = await fetch(url, { cache: 'no-store' })
        const text = await res.text()
        setStockData(text?.trim() ? text : null)
      } catch {
        setStockData(null)
      }
    })()
  }, [featuredProject?.sys?.id, stockData])

  return (
    <div className={getContainerClasses({ position, videoBackground, shorterDisplay })}>
      <div
        className={classNames(
          'flex w-full flex-col items-center gap-4 pb-12 text-center lg:w-fit lg:items-start lg:pb-8',
          {
            'sm:mb-10': !shorterDisplay,
          },
        )}
      >
        {showGuildCta && <FeaturedProjectGuildLabel />}
        <div className="flex w-full items-center justify-center">
          <Image
            alt="Featured Project"
            className="mb-1 !max-w-full"
            src={getCloudinaryImageUrl({
              path: logoUrl ?? '',
              transforms: 'e_trim',
            })}
            style={{ maxHeight: imageHeight, maxWidth: imageWidth }}
            width={imageWidth}
            height={imageHeight}
          />
        </div>
        <div className="w-full text-center">
          <When condition={isGuildMember}>
            {(heading || guildHeading) && <ParagraphLG color="white">{guildHeading ?? heading}</ParagraphLG>}
          </When>
          <When condition={!isGuildMember}>{heading && <ParagraphLG color="white">{heading}</ParagraphLG>}</When>
          {subHeading && <CaptionMD color="white">{subHeading}</CaptionMD>}
        </div>
        {pillLabel && <FeaturedProjectLabel featuredProject={featuredProject} />}
        {showGuildCta && (
          <FeaturedProjectGuildButtonGroup
            {...featuredProject}
            isSubscribedToPifOrGuild={isSubscribedToPifOrGuild}
            isGuildMember={isGuildMember}
            showSketchPink={videoBackground}
          />
        )}
        {featuredProject?.sys?.id === NYSE_FEATURED_PROJECT_ID && stockData && (
          <GBFadeInExperiment
            experimentName="show-stock-price-on-featured-project"
            defaultValue={false}
            className="mx-auto"
          >
            <ExperimentVariation variation={true}>
              <div
                className="backdrop-opacity-2xl mx-auto rounded-2xl p-[2px]"
                style={{
                  background:
                    'linear-gradient(175deg, #EDD7C6 0%, #D6A177 13.88%, #FEDEC6 40.62%, #D48D59 62.21%, #A66141 88.77%, #612B12 98.15%)',
                }}
              >
                <div
                  className="stockContainer w-full max-w-full rounded-xl bg-core-gray-950 p-2 pl-[20px] [&_.updatedDate]:!text-xs [&_.updatedDate]:!text-core-gray-500 [&_h4]:mb-2 [&_h4]:font-semibold [&_h4]:!text-core-gray-500 [&_span]:!text-xl [&_span]:!font-semibold [&_span]:!text-white"
                  dangerouslySetInnerHTML={{ __html: stockData }}
                />
              </div>
            </ExperimentVariation>
            <ExperimentVariation variation={false}>{null}</ExperimentVariation>
            <ExperimentVariation default>{null}</ExperimentVariation>
          </GBFadeInExperiment>
        )}
        {showTicketsCta && <FeaturedProjectTicketsButtonGroup {...featuredProject} showSketchPink={videoBackground} />}
        {showWatchCta && <FeaturedProjectWatchButtonGroup {...featuredProject} showSketchPink={videoBackground} />}
        {showLearnMoreCta && <FeaturedProjectLearnMoreButtonGroup {...featuredProject} />}
        <FeaturedProjectCarousel featuredProject={featuredProject} />
      </div>
    </div>
  )
}

const getContainerClasses = ({
  position,
  videoBackground,
  shorterDisplay,
}: {
  position?: string
  videoBackground: boolean
  shorterDisplay: boolean
}): string => {
  const baseClasses =
    'absolute flex w-full left-1/2 max-w-[800px] -translate-x-1/2 flex-col items-center justify-center px-6'

  const backgroundClasses = {
    'bottom-0 pb-8 sm:pb-0': !videoBackground,
    'top-1/2 -translate-y-1/2': videoBackground,
    'lg:bottom-0': position === 'center' && !videoBackground,
  }

  const leftPositionClasses = position === 'left' && {
    'lg:bottom-0 lg:left-24 lg:right-auto -translate-x-1/2 lg:translate-x-0 2xl:left-40': !videoBackground,
    'lg:top-1/2 lg:left-24 lg:right-auto lg:-translate-y-1/2 lg:translate-x-0 2xl:left-40': videoBackground,
    'lg:-translate-y-1/2 2xl:bottom-12': !shorterDisplay && !videoBackground,
    'lg:-translate-y-1/3': shorterDisplay && !videoBackground,
  }

  const rightPositionClasses = position === 'right' && {
    'lg:bottom-0 lg:left-auto lg:right-24 -translate-x-1/2 lg:-translate-x-0 2xl:right-40': !videoBackground,
    'lg:top-1/2 lg:left-auto lg:right-24 lg:-translate-y-1/2 lg:-translate-x-0 2xl:right-40': videoBackground,
    'lg:-translate-y-1/2 2xl:bottom-12': !shorterDisplay && !videoBackground,
    'lg:-translate-y-1/3': shorterDisplay && !videoBackground,
  }

  return classNames(baseClasses, backgroundClasses, leftPositionClasses, rightPositionClasses)
}

const getImageHeight = (videoBackground: boolean, shorterDisplay: boolean, logoHeight?: Maybe<number>): number => {
  const height = logoHeight ?? 0

  if (videoBackground) {
    return height / 1.5
  }

  if (shorterDisplay) {
    return height / 3
  }

  return height
}

const getImageWidth = (videoBackground: boolean, shorterDisplay: boolean, logoWidth?: Maybe<number>): number => {
  const width = logoWidth ?? 0

  if (videoBackground) {
    return width / 1.5
  }

  if (shorterDisplay) {
    return width / 3
  }

  return width
}
